import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowRight } from 'lucide-react';
import { heroData } from '../../data/mock';
import conflictVideo from '@/assets/images/conflict.mp4';
import happyVideo from '@/assets/images/happy.mp4';

const HeroSection = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#efede6] pt-24 pb-20">
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#e8e5dd] via-[#efede6] to-[#efede6] opacity-60" />

      <div className="relative z-10 max-w-6xl mx-auto px-8">
        {/* Main Content */}
        <div className="text-center mb-16">
          {/* Badge */}
          <div className={`inline-flex items-center gap-2 border border-[#202020]/15 rounded-lg px-5 py-2 mb-10 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="text-sm text-[#202020]/60 tracking-wide">Mind Fitness for Moms</span>
          </div>

          {/* Main Headline */}
          <h1 className={`text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal text-[#202020] leading-[1.1] mb-8 tracking-tight transition-all duration-1000 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            Move from <em className="font-serif italic">conflict</em> to a{' '}
            <em className="font-serif italic">peaceful</em>, friendly relationship with your teen
          </h1>

          {/* Subheadline */}
          <p className={`text-lg md:text-xl text-[#202020]/60 max-w-2xl mx-auto mb-12 leading-relaxed transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {heroData.subheadline}
          </p>

          {/* CTA */}
          <div className={`transition-all duration-1000 delay-450 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <Button 
              onClick={() => navigate('/checkout')}
              className="bg-[#202020] hover:bg-[#303030] text-[#efede6] text-base px-10 py-6 rounded-lg shadow-lg transition-all duration-300 hover:scale-105 group"
            >
              {heroData.ctaText}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>

        {/* Transformation Journey - 3 Images */}
        <div className={`transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-4">
            {/* Stage 1: Conflict (B&W) */}
            <div className="relative group flex-1 max-w-lg">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <video 
                  src={conflictVideo} 
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-84 md:h-96 object-cover grayscale brightness-90"
                />
                {/* <div className="absolute inset-0 bg-[#202020]/20" /> */}
              </div>
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#202020] text-[#efede6] text-xl px-4 py-1.5 rounded-lg whitespace-nowrap">
                Conflict
              </div>
            </div>

            {/* Arrow */}
            <div className="hidden md:flex items-center justify-center px-2">
              <div className="w-10 h-10 rounded-lg border border-[#202020]/20 flex items-center justify-center">
                <ArrowRight className="w-4 h-4 text-[#202020]/30" />
              </div>
            </div>
            
            {/* Stage 3: Peace (Full Color) */}
            <div className="relative group flex-1 max-w-lg">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <video 
                  src={happyVideo} 
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-84 md:h-96 object-cover grayscale brightness-90"
                />
              </div>
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#202020] text-[#efede6] text-xl px-4 py-1.5 rounded-lg whitespace-nowrap">
                Peace
              </div>
            </div>
          </div>

          {/* Journey Label */}
          <p className={`text-center text-[#202020]/40 text-sm mt-10 transition-all duration-1000 delay-800 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            
          </p>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2">
        <div className="w-6 h-10 border border-[#202020]/30 rounded-lg flex justify-center pt-2">
          <div className="w-1 h-2 bg-[#202020]/40 rounded-lg animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
